import type { IssuedQuoteVersion } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount, formatDateTime, formatWon } from '@/shared/lib';
import { ContentCard, cn, detailListStyles as styles, tableStyles } from '@/shared/ui';

interface OrderQuoteContentsProps {
  readonly quote: IssuedQuoteVersion;
}

const NUMBER_CELL_CLASS = 'text-right whitespace-nowrap tabular-nums';

/**
 * 주문 내용 — 주문한 발행본의 품목 표(서버 줄 순서: 상품 → 배송비)·금액(상품 금액·배송비·공급가액·부가세·합계, SUPER-86 §7)·
 * 제작/납기 조건·약관 전문(고객이 동의한 그대로). 약관 버전(저장값)은 운영자 기록이라 여기에만 보여 준다.
 */
export const OrderQuoteContents = ({ quote }: OrderQuoteContentsProps): ReactElement => (
  <ContentCard
    title={`주문 내용 · 발행본 v${quote.version}`}
    description={`발행 일시 ${formatDateTime(quote.issuedAt)} · 유효 기한 ${quote.validUntil}`}
  >
    <div className="overflow-x-auto">
      <table className={tableStyles.table}>
        <thead className={tableStyles.head}>
          <tr>
            <th className={tableStyles.th}>품목</th>
            <th className={cn(tableStyles.th, 'text-right')}>수량</th>
            <th className={cn(tableStyles.th, 'text-right')}>단가</th>
            <th className={cn(tableStyles.th, 'text-right')}>금액</th>
          </tr>
        </thead>
        <tbody>
          {quote.lines.map((line) => (
            <tr key={line.id} className={tableStyles.row}>
              <td className={tableStyles.td}>{line.name}</td>
              <td className={cn(tableStyles.td, NUMBER_CELL_CLASS)}>
                {formatCount(line.quantity)}
              </td>
              <td className={cn(tableStyles.td, NUMBER_CELL_CLASS)}>{formatWon(line.unitPrice)}</td>
              <td className={cn(tableStyles.td, NUMBER_CELL_CLASS)}>{formatWon(line.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <dl className="mt-4 ml-auto grid max-w-xs grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-t border-foreground/10 pt-4 text-sm tabular-nums">
      <dt className={styles.term}>상품 금액</dt>
      <dd className="text-right">{formatWon(quote.productAmount)}</dd>
      <dt className={styles.term}>배송비</dt>
      <dd className="text-right">{formatWon(quote.shippingAmount)}</dd>
      <dt className={styles.term}>공급가액</dt>
      <dd className="text-right">{formatWon(quote.supplyAmount)}</dd>
      <dt className={styles.term}>부가세</dt>
      <dd className="text-right">{formatWon(quote.vatAmount)}</dd>
      <dt className="font-semibold">합계</dt>
      <dd className="text-right font-semibold">{formatWon(quote.totalAmount)}</dd>
    </dl>

    <dl className={cn(styles.list, 'mt-6')}>
      <dt className={styles.term}>제작 착수 시점</dt>
      <dd>{quote.productionStartNote}</dd>
      <dt className={styles.term}>납기</dt>
      <dd>{quote.deliveryNote}</dd>
      <dt className={styles.term}>공급 방법</dt>
      <dd>{quote.deliveryMethodNote}</dd>
      <dt className={styles.term}>특약 사항</dt>
      <dd className="whitespace-pre-wrap">
        {quote.specialTerms ?? <span className={styles.empty}>없음</span>}
      </dd>
      <dt className={styles.term}>약관 버전</dt>
      <dd>{quote.termsVersion}</dd>
    </dl>

    <h3 className={cn('mt-6', styles.subheading)}>약관</h3>
    <p className="mt-2 max-h-48 overflow-y-auto rounded-xl bg-foreground/[0.03] p-4 text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground">
      {quote.termsText}
    </p>
  </ContentCard>
);
